import React, { useEffect, useState } from 'react';
import api from '../services/api';

interface TriboAtiva {
  id_tribo: number;
  slug: string;
  nome: string;
  tags: string[];
  oficial: boolean;
  estado: 'ativa' | 'bloqueada';
  icone_url: string | null;
  data_aprovacao: string | null;
  data_bloqueio: string | null;
  motivo_bloqueio: string | null;
  membros_count: number;
  posts_7d: number;
}

const TribosAtivas: React.FC = () => {
  const [items, setItems] = useState<TriboAtiva[]>([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchAtivas('');
  }, []);

  const fetchAtivas = async (query: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/admin/tribos/ativas', {
        params: query ? { q: query } : undefined,
      });
      setItems(response.data.items ?? []);
    } catch (err) {
      console.error('Erro ao buscar tribos ativas:', err);
      setError('Erro ao carregar.');
    } finally {
      setLoading(false);
    }
  };

  const bloquear = async (id_tribo: number, nome: string) => {
    const motivo = window.prompt(
      `Bloquear "${nome}". Informe o motivo (mínimo 30 caracteres; será enviado por push/email/WhatsApp aos admins da tribo):`,
    );
    if (!motivo || motivo.trim().length < 30) {
      if (motivo !== null) alert('Motivo precisa ter ao menos 30 caracteres.');
      return;
    }
    setActionLoading(true);
    try {
      await api.post(`/admin/tribos/${id_tribo}/bloquear`, { motivo: motivo.trim() });
      alert('Tribo bloqueada.');
      fetchAtivas(q);
    } catch (err: any) {
      alert(err?.response?.data?.error ?? 'Erro ao bloquear.');
    } finally {
      setActionLoading(false);
    }
  };

  const reativar = async (id_tribo: number, nome: string) => {
    if (!window.confirm(`Reativar "${nome}"?`)) return;
    setActionLoading(true);
    try {
      await api.post(`/admin/tribos/${id_tribo}/reativar`);
      alert('Tribo reativada.');
      fetchAtivas(q);
    } catch (err: any) {
      alert(err?.response?.data?.error ?? 'Erro ao reativar.');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <header style={styles.headerRow}>
        <h1 style={styles.title}>Tribos ativas / bloqueadas</h1>
        <div style={styles.searchWrap}>
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') fetchAtivas(q);
            }}
            placeholder="Buscar por nome ou slug…"
            style={styles.search}
          />
          <button onClick={() => fetchAtivas(q)} style={styles.searchButton}>
            Buscar
          </button>
        </div>
      </header>

      {error && <p style={{ ...styles.message, color: '#dc3545' }}>{error}</p>}
      {loading && <p style={styles.message}>Carregando…</p>}
      {!loading && items.length === 0 && (
        <p style={styles.message}>Nenhuma tribo encontrada.</p>
      )}

      <div style={styles.list}>
        {items.map((t) => (
          <div key={t.id_tribo} style={styles.card}>
            <div style={styles.cardIconWrap}>
              {t.icone_url ? (
                <img src={t.icone_url} alt="" style={styles.cardIcon} />
              ) : (
                <div style={styles.cardIconPlaceholder}>{t.nome[0]?.toUpperCase()}</div>
              )}
            </div>
            <div style={styles.cardBody}>
              <div style={styles.cardHeader}>
                <span style={styles.estadoBadge(t.estado)}>{t.estado.toUpperCase()}</span>
                {(t.tags ?? []).map((tag) => (
                  <span key={tag} style={styles.tipoBadge}>#{tag}</span>
                ))}
              </div>
              <h3 style={styles.cardTitle}>
                <a
                  href={`https://tribhus.com.br/tribos/${t.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={styles.cardLink}
                >
                  {t.nome}
                </a>
              </h3>
              <p style={styles.cardMeta}>
                /tribos/{t.slug} · {t.membros_count} membros · {t.posts_7d} posts (7d)
                {t.data_aprovacao && (
                  <> · aprovada {new Date(t.data_aprovacao).toLocaleDateString('pt-BR')}</>
                )}
              </p>
              {t.estado === 'bloqueada' && t.motivo_bloqueio && (
                <p style={styles.motivoBloqueio}>
                  <strong>Bloqueada:</strong> {t.motivo_bloqueio}
                  {t.data_bloqueio && (
                    <> · em {new Date(t.data_bloqueio).toLocaleDateString('pt-BR')}</>
                  )}
                </p>
              )}
            </div>
            <div style={styles.cardActions}>
              {t.estado === 'ativa' ? (
                <button
                  onClick={() => bloquear(t.id_tribo, t.nome)}
                  disabled={actionLoading}
                  style={{ ...styles.actionButton, backgroundColor: '#dc3545' }}
                >
                  Bloquear
                </button>
              ) : (
                <button
                  onClick={() => reativar(t.id_tribo, t.nome)}
                  disabled={actionLoading}
                  style={{ ...styles.actionButton, backgroundColor: '#28a745' }}
                >
                  Reativar
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const styles: { [key: string]: any } = {
  container: {
    padding: '20px',
    maxWidth: '1200px',
    margin: '0 auto',
    backgroundColor: '#f9f9f9',
    borderRadius: '8px',
    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap' as const,
    gap: 12,
    marginBottom: 20,
  },
  title: { fontSize: 24, color: '#333', margin: 0 },
  searchWrap: { display: 'flex', gap: 8 },
  search: {
    padding: '8px 12px',
    border: '1px solid #ced4da',
    borderRadius: 4,
    fontSize: 14,
    minWidth: 240,
  },
  searchButton: {
    padding: '8px 16px',
    border: 'none',
    borderRadius: 4,
    backgroundColor: '#007bff',
    color: '#fff',
    fontSize: 14,
    cursor: 'pointer',
  },
  message: { fontSize: 16, textAlign: 'center', color: '#555', padding: '40px 0' },
  list: { display: 'flex', flexDirection: 'column' as const, gap: 12 },
  card: {
    display: 'flex',
    gap: 16,
    padding: 16,
    backgroundColor: '#fff',
    borderRadius: 8,
    border: '1px solid #e0e0e0',
    alignItems: 'center',
  },
  cardIconWrap: { width: 56, height: 56, flexShrink: 0 },
  cardIcon: { width: 56, height: 56, borderRadius: 8, objectFit: 'cover' },
  cardIconPlaceholder: {
    width: 56,
    height: 56,
    borderRadius: 8,
    backgroundColor: '#6c757d',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 22,
    fontWeight: 'bold',
  },
  cardBody: { flex: 1, minWidth: 0 },
  cardHeader: { display: 'flex', gap: 6, marginBottom: 6 },
  estadoBadge: (estado: string) => ({
    padding: '2px 8px',
    borderRadius: 4,
    fontSize: 10,
    fontWeight: 'bold',
    color: '#fff',
    backgroundColor: estado === 'bloqueada' ? '#dc3545' : '#28a745',
  }),
  tipoBadge: {
    padding: '2px 8px',
    borderRadius: 4,
    fontSize: 10,
    backgroundColor: '#e9ecef',
    color: '#495057',
  },
  cardTitle: { fontSize: 16, margin: '4px 0', color: '#212529' },
  cardLink: { color: '#212529', textDecoration: 'none' },
  cardMeta: { fontSize: 12, color: '#6c757d', margin: '4px 0 0' },
  motivoBloqueio: {
    fontSize: 12,
    color: '#721c24',
    backgroundColor: '#f8d7da',
    border: '1px solid #f5c6cb',
    borderRadius: 4,
    padding: '6px 10px',
    margin: '6px 0 0',
  },
  cardActions: { display: 'flex' },
  actionButton: {
    padding: '8px 14px',
    border: 'none',
    borderRadius: 4,
    color: '#fff',
    fontSize: 13,
    cursor: 'pointer',
    minWidth: 100,
  },
};

export default TribosAtivas;
